import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { ROUTE_ADMIN_ADD_NEWS, ROUTE_NEWS } from 'CorePackage/MainModule/routes/routes';
import { DATE_FORMAT_SHORT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { mapNewsStatus } from 'NewsPackage/FormModule/common/mapNewsStatus';
import { mapNewsType } from 'NewsPackage/FormModule/common/mapNewsType';
import { INewsClipped } from 'APIPackage/NewsModule/interfaces/INewsClipped';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getSortIcon } from 'CorePackage/MainModule/common/getSortIcon';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { INews } from 'APIPackage/NewsModule/interfaces/INews';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getSortOrder } from 'CorePackage/MainModule/common/getSortOrder';

import { initialNewsTableColumns } from '../constants/initialNewsTableColumns';
import styles from './NewsTable.module.scss';
import { adminGetNews, adminGetNewsNextPage, adminSetNewsOrder } from '../actions/adminAction';
import { getAdminNews, getAdminNewsTotalCount } from '../selectors/adminSelectors';
import { newsOrder } from '../constants/newsOrder';

interface INewsTableStoreProps {
  news: INews[];
  totalCount: number;
}

const NewsTableWithoutStore = ({ news, totalCount }: INewsTableStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const history = useHistory();
  const [tableColumns, setTableColumns] = useState<ITableColumn<INewsClipped>[]>(initialNewsTableColumns);

  useEffect(() => {
    dispatch(adminGetNews());
  }, [dispatch]);

  const onAddNewsClick = useCallback(() => {
    history.push(ROUTE_ADMIN_ADD_NEWS);
  }, [history]);

  const handleSortClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const { id } = e.currentTarget.dataset;
      const newColumnsData = handleSortChange(tableColumns, id as keyof INewsClipped);
      const newOrder = getSortOrder(newColumnsData);

      setTableColumns(newColumnsData);
      dispatch(adminSetNewsOrder(newOrder || newsOrder));
      dispatch(adminGetNews());
    },
    [dispatch, tableColumns],
  );

  const onLoadMore = useCallback(() => dispatch(adminGetNewsNextPage()), [dispatch]);

  const makeOnNewsClickHandler = (id: string | number) => () => {
    history.push(`${ROUTE_NEWS}/${id}`);
  };

  return (
    <div className={styles.TableWrapper}>
      <div className={styles.Header}>
        <h1 className={styles.Title}>Таблица новостей</h1>
        <Button
          className={styles.AddButton}
          color="success"
          text="Добавить новость"
          id="addButton"
          onClick={onAddNewsClick}
        />
      </div>
      <table className={styles.Table}>
        <thead>
          <tr className={styles.Row}>
            {tableColumns.map((row, i) => (
              <th onClick={handleSortClick} data-id={row.id} className={styles.CheckboxTheadCell} key={i}>
                {row.name}
                {getSortIcon(tableColumns, row.id)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {news.map((row, i) => (
            <tr className={styles.TbodyRow} key={i} onClick={makeOnNewsClickHandler(row.id)}>
              <td className={styles.CheckboxTbodyCell}>{mapNewsType(row.type)}</td>
              <td className={styles.CheckboxTbodyCell}>{mapNewsStatus(row.status)}</td>
              <td className={styles.CheckboxTbodyCell}>{row.title}</td>
              <td className={styles.CheckboxTbodyCell}>{row.shortText}</td>
              <td className={styles.CheckboxTbodyCell}>
                {row.published ? formatDate({ date: row.published, dateFormat: DATE_FORMAT_SHORT_FNS }) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {news.length < totalCount && <Button type="button" text="Показать еще" color="load" onClick={onLoadMore} />}
    </div>
  );
};

const mapStateToProps = (state: IRootState): INewsTableStoreProps => ({
  news: getAdminNews(state),
  totalCount: getAdminNewsTotalCount(state),
});

export const NewsTable = connect(mapStateToProps)(NewsTableWithoutStore);
